import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { sanitizeFileName } from '../utils/FileAccessUtils';
import { ChecklistEvaluation, ChecklistExtraction } from '../types/ComplianceChecklistTypes';
import { ComplianceDocument } from '../types/ComplianceTypes';

const fetchUtils = new FetchUtils();

// The controller's body parser caps the sample at 10MB, and the endpoint reads a
// PDF (Gemini takes it natively) — both enforced client-side too so a bad file is
// rejected before it is uploaded twice.
export const COMPLIANCE_SAMPLE_MAX_FILE_SIZE_MB = 10;
export const COMPLIANCE_SAMPLE_ACCEPTED_MIME_TYPES = ['application/pdf'];

const EXTRACTION_FAILURE_MESSAGE = 'We could not read this document.';
const EVALUATION_FAILURE_MESSAGE = 'We could not check this document against the checklist.';

// POST /api/v1/{role}/compliance/requests/sample_pdf/extract_data
//
// Multipart, not JSON: the sample file itself goes up as the `file` part (an
// optional `prompt` part can override the server's configured extraction prompt;
// we don't send one). The endpoint is a stateless Gemini proxy — the model's JSON
// comes back verbatim under `data` and is not stored server-side, so there is no
// reducer slice for it. The reviewed extraction is persisted by riding along on
// the compliance request create/update as `checklistExtraction`.
//
// Sent with FetchUtils.postFormData so the multipart body goes up untouched while
// the shared read-only / impersonation write guards and the auth header still
// apply — request() itself can't carry this one, since it JSON-stringifies every
// body and pins Content-Type.
export const extractComplianceChecklistFromSample =
	(userType) =>
	(sampleFile: File) =>
	(dispatch): Promise<ChecklistExtraction> => {
		const apiUrl = `${getBackendUri()}/api/v1/${userType}/compliance/requests/sample_pdf/extract_data`;
		const formData = new FormData();
		formData.append('file', sampleFile, sanitizeFileName(sampleFile.name));
		return fetchUtils
			.postFormData(apiUrl, formData)
			.then(parseJSON)
			.then(({ json, ok, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
					return Promise.reject('unauthorized');
				}
				// 400 (no file / model returned nothing) and 403 (not permitted to run
				// extraction) both come back as the standard error envelope.
				if (!ok || nullSafeGet('status', json) === 'error') {
					return Promise.reject(nullSafeGetOrElse('message', json, EXTRACTION_FAILURE_MESSAGE));
				}
				const extraction = nullSafeGet('data', json);
				return extraction ? extraction : Promise.reject(EXTRACTION_FAILURE_MESSAGE);
			});
	};

// POST /api/v1/supplier/compliance/request_instances/:id/document/extract_data
//
// Checks a supplier's document against the checklist stored on that instance's
// compliance request. Multipart like the extraction endpoint: only the file goes
// up. We deliberately do not send the checklist or the supplier's legal name —
// the backend loads the stored `checklistExtraction` and substitutes
// {{supplier.legal_name}} and the evaluation date from platform records, so the
// supplier cannot influence what they are measured against.
//
// Stateless, like extraction: the model's JSON comes back verbatim under `data`
// and nothing is persisted until link_document / replace_document carries it up
// as `checklistEvaluation`. No reducer slice — the caller holds the result until
// the supplier decides to link.
//
// The response has no overall verdict; derive it with
// deriveOverallEvaluationStatus (ComplianceEvaluationUtils).
//
// Errors: 404 when the instance is not the caller's or is still Draft, 400 when
// the request has no stored checklist, the file is missing, the caller is an
// internal service team, or the model fails.
export const evaluateComplianceDocumentAgainstChecklist =
	(userType) =>
	(instanceId: number, documentFile: File) =>
	(dispatch): Promise<ChecklistEvaluation> => {
		const apiUrl = `${getBackendUri()}/api/v1/${userType}/compliance/request_instances/${instanceId}/document/extract_data`;
		const formData = new FormData();
		formData.append('file', documentFile, sanitizeFileName(documentFile.name));
		return fetchUtils
			.postFormData(apiUrl, formData)
			.then(parseJSON)
			.then(({ json, ok, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
					return Promise.reject('unauthorized');
				}
				if (!ok || nullSafeGet('status', json) === 'error') {
					return Promise.reject(nullSafeGetOrElse('message', json, EVALUATION_FAILURE_MESSAGE));
				}
				const evaluation = nullSafeGet('data', json);
				return evaluation ? evaluation : Promise.reject(EVALUATION_FAILURE_MESSAGE);
			});
	};

// Why this file cannot be sent to the evaluate endpoint, or null when it can.
// The endpoint reads a PDF and its body parser caps the upload, so both are
// checked here rather than spending a round-trip on a guaranteed 400. A supplier
// whose document fails these still links it — the AI check is help, not a gate.
export const describeUnevaluatableFile = (file: File): string | null => {
	if (!file) {
		return 'We could not read this document, so it was not checked.';
	}
	const isPdf =
		COMPLIANCE_SAMPLE_ACCEPTED_MIME_TYPES.indexOf(file.type) !== -1 ||
		/\.pdf$/i.test(nullSafeGetOrElse('name', file, ''));
	if (!isPdf) {
		return 'Only PDF documents can be checked automatically, so this one was not checked.';
	}
	if (file.size / 1024 / 1024 >= COMPLIANCE_SAMPLE_MAX_FILE_SIZE_MB) {
		return `Documents larger than ${COMPLIANCE_SAMPLE_MAX_FILE_SIZE_MB}MB cannot be checked automatically.`;
	}
	return null;
};

// Re-reads an already-uploaded compliance document as a File, so a document the
// supplier picked from their library can be evaluated — the evaluate endpoint
// takes bytes, not a document id.
//
// Reads the stored fileUrl (the CDN) directly rather than the backend's
// file/download route: that route rejects any request carrying an Origin header,
// so it 401s from the browser for suppliers — the "FIXME: make this work for
// supplier" on getFile. The CDN answers cross-origin reads with the right
// access-control headers, which is why downloadImages in ImageUtils also tries
// the CDN first.
//
// Rejects on anything unreadable; callers treat that as "could not check" and
// offer to link anyway.
//
// BACKEND FOLLOW-UP: if document/extract_data accepted a complianceDocumentId,
// this whole download-and-re-upload round-trip would go away — the server
// already has the file.
export const fetchComplianceDocumentAsFile = (
	complianceDocument: ComplianceDocument
): Promise<File> => {
	const fileUrl = nullSafeGet('fileUrl', complianceDocument);
	if (!fileUrl) {
		return Promise.reject('This document has no file to check.');
	}
	// The stored name is the one the supplier sees; fall back to the url's last
	// segment so the multipart part is never named "undefined".
	const urlFileName = decodeURIComponent(fileUrl.split('?')[0].split('/').pop() || '');
	const fileName = nullSafeGetOrElse('fileName', complianceDocument, urlFileName || 'document.pdf');
	return fetch(fileUrl)
		.then((response) => {
			if (!response.ok) {
				return Promise.reject('This document could not be downloaded for checking.');
			}
			return response.blob();
		})
		.then((blob: Blob) => new File([blob], fileName, { type: blob.type }));
};
